import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { LocalizationProvider, TimePicker as MUITimePicker, TimePickerProps } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';

type TimePrickerExtendsType = Omit<TimePickerProps<any>, 'open' | 'onChange' | 'onAccept' | 'openPicker' | 'rawValue'>;

interface Props extends TimePrickerExtendsType {
  sx?: TimePickerProps<any>['sx'];
  focused?: boolean;
  inputLabel?: string;
  error?: boolean | string;
  className?: string;
  onAccept?: (date: Date | null) => void;
  onClear?: () => void;
  id?: string;
}

export const TimePicker: FC<Props> = ({ sx = {}, focused, inputLabel, error, onAccept, onClear, id, ...props }) => {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof focused === 'undefined') {
      return;
    }

    setOpen(focused);
  }, [focused]);

  const handleChange = (date: Date | null) => {
    try {
      if (onClear && !date) {
        onClear();
        return;
      }

      if (onAccept && moment(date).isValid()) {
        onAccept(date);
      }
    } catch {
      if (!onClear) {
        return;
      }

      onClear();
    }
  };

  const handleAccept = (date: Date | null) => {
    if (!onAccept) {
      return;
    }

    onAccept(date);
    setOpen(false);
  };

  return (
    <Box id={id} sx={sx} position="relative">
      {inputLabel && <label className="small mb-2 fw-medium d-block required">{inputLabel}</label>}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MUITimePicker
          {...props}
          slotProps={{
            textField: { color: error ? 'error' : undefined, error: !!error, helperText: error, size: 'small' },
          }}
          open={open}
          onAccept={handleAccept as any}
          onChange={handleChange as any}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
        />
      </LocalizationProvider>
    </Box>
  );
};
